import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mammoth from 'mammoth';
import DOMPurify from 'dompurify';
import {
  getSubmission,
  updateReview,
  fetchFileBlob,
  confirmPayment,
  rejectPayment,
  updateSubmissionStatus,
  sendToReviewer,
  publishSubmission,
  runAIReview,
  getReviewers,
  markAsRead,
  updateSubmissionMeta,
  downloadCertificates,
} from '../api/client';
import { StatusBadge, PaymentBadge } from '../components/StatusBadge';
import { useAuth } from '../App';
import AuditLogModal from './AuditLogModal';

const LANG_OPTIONS = [
  { value: 'uz', label: "O'zbekcha" },
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
];

// dd.mm.yyyy format
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

// Determine file type from filename
const getFileType = (filename) => {
  if (!filename) return 'unknown';
  const ext = filename.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'docx';
  if (['tex'].includes(ext)) return 'tex';
  return 'unknown';
};

export default function SubmissionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedJournalSlug, currentJournal } = useAuth();
  const [searchParams] = useSearchParams();
  const issueId = searchParams.get('issue');
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('uz');
  const [actionLoading, setActionLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [aiReview, setAiReview] = useState(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiCountdown, setAiCountdown] = useState(0);
  const [reviewers, setReviewers] = useState([]);
  const [showReviewerSelect, setShowReviewerSelect] = useState(false);
  const [commentModal, setCommentModal] = useState(null); // {type: 'revision_required' | 'rejected', title: '...'}
  const [showAuditLog, setShowAuditLog] = useState(false);

  const fetchSubmission = async () => {
    setLoading(true);
    try {
      const res = await getSubmission(id);
      setSub(res.data);
      // Auto-mark as read
      if (!res.data.is_read) {
        try { await markAsRead(id); } catch (e) { /* ignore */ }
      }
    } catch (err) {
      toast.error('Maqola topilmadi');
      navigate('/submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmission(); }, [id, selectedJournalSlug]);

  useEffect(() => {
    return () => {
      if (previewFile?.objectUrl) {
        URL.revokeObjectURL(previewFile.objectUrl);
      }
    };
  }, [previewFile]);

  // AI review countdown timer
  useEffect(() => {
    if (aiCountdown <= 0) return;
    const timer = setInterval(() => {
      setAiCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [aiCountdown]);

  // ── Multilingual helpers ──
  const getField = (baseField, enField, ruField) => {
    if (lang === 'en' && enField) return enField;
    if (lang === 'ru' && ruField) return ruField;
    return baseField || '-';
  };

  const getAuthorName = (person) => {
    if (!person) return '-';
    if (lang === 'en' && person.last_name_en) {
      return `${person.last_name_en} ${person.first_name_en || ''} ${person.middle_name_en || ''}`.trim();
    }
    if (lang === 'ru' && person.last_name_ru) {
      return `${person.last_name_ru} ${person.first_name_ru || ''} ${person.middle_name_ru || ''}`.trim();
    }
    return `${person.last_name} ${person.first_name} ${person.middle_name || ''}`.trim();
  };

  const getAffiliation = (person) => {
    if (!person) return '-';
    if (lang === 'en' && person.affiliation_en) return person.affiliation_en;
    if (lang === 'ru' && person.affiliation_ru) return person.affiliation_ru;
    return person.affiliation || '-';
  };

  const closePreview = () => {
    setPreviewFile((prev) => {
      if (prev?.objectUrl) {
        URL.revokeObjectURL(prev.objectUrl);
      }
      return null;
    });
  };

  const openPreview = async (storedName, originalName) => {
    if (!storedName) return;
    const type = getFileType(storedName);
    try {
      const res = await fetchFileBlob(storedName, type !== 'docx');
      const objectUrl = URL.createObjectURL(res.data);
      let docxHtml = null;
      if (type === 'docx') {
        const arrayBuffer = await res.data.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        docxHtml = DOMPurify.sanitize(result.value, {
          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li',
                          'h1', 'h2', 'h3', 'h4', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'span'],
          ALLOWED_ATTR: []
        });
      }
      setPreviewFile((prev) => {
        if (prev?.objectUrl) {
          URL.revokeObjectURL(prev.objectUrl);
        }
        return {
          previewUrl: objectUrl,
          downloadUrl: objectUrl,
          objectUrl,
          name: originalName || storedName,
          type,
          docxHtml,
        };
      });
    } catch (err) {
      let message = "Faylni ochib bo'lmadi";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          message = json.detail || message;
        } catch {}
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      }
      toast.error(message);
    }
  };

  const handleCompleteReview = async (reviewId, result) => {
    const reviewComment = prompt('Taqrizchi izohi (ixtiyoriy):');
    try {
      await updateReview(id, reviewId, { review_result: result, comment: reviewComment || '' });
      toast.success('Taqriz yangilandi');
      fetchSubmission();
    } catch (err) {
      toast.error('Xatolik');
    }
  };

  const handleConfirmPayment = async () => {
    setActionLoading(true);
    try {
      await confirmPayment(id);
      toast.success("To'lov tasdiqlandi!");
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPayment = async () => {
    const reason = prompt("To'lov habarnomasini rad etish sababini kiriting (ixtiyoriy):");
    if (reason === null) return; // cancelled
    setActionLoading(true);
    try {
      await rejectPayment(id, reason || undefined);
      toast.success("To'lov habarnomasi rad etildi");
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Status change handlers ──
  const handleStatusChange = async (newStatus, comment = '') => {
    setActionLoading(true);
    try {
      await updateSubmissionStatus(id, { status: newStatus, admin_comment: comment || undefined });
      toast.success("Status o'zgartirildi");
      setCommentModal(null);
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAIReview = async () => {
    setAiReviewLoading(true);
    setAiReview(null);
    setAiCountdown(45);
    try {
      const res = await runAIReview(id);
      setAiReview(res.data.review);
      // Refresh submission to get persisted ai_review_text
      fetchSubmission();
      // Load reviewers list
      const revRes = await getReviewers();
      setReviewers(revRes.data);
      setShowReviewerSelect(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI taqriz xatolik');
    } finally {
      setAiReviewLoading(false);
      setAiCountdown(0);
    }
  };

  const handleSendToReviewer = async (reviewer) => {
    setActionLoading(true);
    try {
      await sendToReviewer(id, {
        reviewer_name: `${reviewer.last_name} ${reviewer.first_name} ${reviewer.middle_name || ''}`.trim(),
        reviewer_email: reviewer.email,
      });
      toast.success("Taqrizchiga yuborildi");
      setShowReviewerSelect(false);
      setAiReview(null);
      fetchSubmission();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Xatolik');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptDirect = async () => {
    if (!window.confirm("Maqolani taqrizsiz qabul qilmoqchimisiz?")) return;
    await handleStatusChange('accepted');
  };

  const handlePublish = async (force = false) => {
    if (!force && !window.confirm("Maqolani chop etishga yuborishni tasdiqlaysizmi?")) return;
    setActionLoading(true);
    try {
      const res = await publishSubmission(id, force);
      toast.success(res.data?.message || "Chop etishga yuborildi");
      fetchSubmission();
    } catch (err) {
      if (err.response?.status === 409) {
        // Payment not confirmed — ask admin to force
        if (window.confirm(err.response.data.detail + "\n\nTo'lovsiz davom etasizmi?")) {
          setActionLoading(false);
          return handlePublish(true);
        }
      } else {
        toast.error(err.response?.data?.detail || 'Xatolik');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleBack = () => {
    if (issueId) {
      navigate(`/submissions?issue=${issueId}`);
    } else {
      navigate('/submissions');
    }
  };

  // Trigger certificate ZIP download for the current submission.
  // ``kind`` is "published" or "accepted"; the backend renders a fresh PDF
  // per author and returns a ZIP. We save it via the browser using a temp
  // anchor — works cross-browser without extra deps.
  const handleDownloadCertificates = async (kind) => {
    setActionLoading(true);
    const dismiss = toast.loading('Sertifikatlar tayyorlanmoqda…');
    try {
      const res = await downloadCertificates(id, kind);
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = (m && m[1]) || `Certificates_Sub${id}_${kind}.zip`;
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const count = res.headers?.['x-author-count'];
      toast.success(`Sertifikatlar yuklab olindi${count ? ` (${count} ta muallif)` : ''}`, { id: dismiss });
    } catch (err) {
      // For binary blob responses, axios may still hand us a Blob with the
      // error JSON inside — try to read it for a friendlier message.
      let detail = "Sertifikat tayyorlashda xatolik";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } catch (_) { /* keep default */ }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(detail, { id: dismiss });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
    </div>
  );
  if (!sub) return null;

  const fileStoredName = sub.file_path || null;
  const receiptStoredName = sub.payment_receipt_path || null;
  const revisedStoredName = sub.revised_file_path || null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={handleBack}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium mb-2 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Orqaga
          </button>
          <h2 className="text-xl font-bold text-slate-900">#{sub.id}: {sub.title}</h2>
          <p className="mt-1 text-sm text-slate-500">
            {currentJournal?.short_name || currentJournal?.name || 'Tanlangan jurnal'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sub.source_format && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
              sub.source_format === 'tex'
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-blue-100 text-blue-700 border border-blue-200'
            }`}>
              {sub.source_format === 'tex' ? '📝 LaTeX' : '📄 Word'}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowAuditLog(true)}
            className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
            title="Admin amallari tarixi"
          >
            📜 Audit log
          </button>
          <StatusBadge status={sub.status} />
        </div>
      </div>

      {showAuditLog && (
        <AuditLogModal
          submissionId={sub.id}
          onClose={() => setShowAuditLog(false)}
        />
      )}

      {/* Dates Block */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6 px-6 py-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Sanalar</h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase">Yuborilgan</p>
              <p className="text-sm font-medium text-slate-800">{formatDate(sub.submitted_at) || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase">Taqrizga yuborilgan</p>
              <p className="text-sm font-medium text-slate-800">{formatDate(sub.sent_to_review_at) || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase">Qabul qilingan</p>
              <p className="text-sm font-medium text-slate-800">{formatDate(sub.accepted_at) || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Language selector */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6 px-6 py-3 flex items-center gap-3">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Til:</span>
        <div className="flex gap-1">
          {LANG_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLang(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                lang === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Authors Section */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Mualliflar</h3>
        </div>
        <div className="p-6 space-y-4">
          {/* Main Author */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">Asosiy muallif</span>
            </div>
            <p className="text-sm font-semibold text-slate-900">{getAuthorName(sub.author)}</p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>📧 {sub.author?.email || '-'}</span>
              <span>📞 {sub.author?.phone || '-'}</span>
              {sub.author?.orcid && <span>🆔 ORCID: {sub.author.orcid}</span>}
              <span>🏫 {getAffiliation(sub.author)}</span>
              <span>🌍 {sub.author?.country || '-'}</span>
            </div>
          </div>

          {/* Co-authors */}
          {sub.coauthors && sub.coauthors.length > 0 && sub.coauthors.map((ca, idx) => (
            <div key={ca.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-500 text-white">Hammuallif {idx + 1}</span>
              </div>
              <p className="text-sm font-semibold text-slate-900">{getAuthorName(ca)}</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-500">
                <span>📧 {ca.email || '-'}</span>
                <span>📞 {ca.phone || '-'}</span>
                {ca.orcid && <span>🆔 ORCID: {ca.orcid}</span>}
                <span>🏫 {getAffiliation(ca)}</span>
                <span>🌍 {ca.country || '-'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Article Content */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Maqola ma'lumotlari</h3>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Maqola nomi</label>
            <p className="text-sm font-medium text-slate-900 mt-1">
              {getField(sub.title, sub.title_en, sub.title_ru)}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Annotatsiya</label>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">
              {getField(sub.abstract, sub.abstract_en, sub.abstract_ru)}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Kalit so'zlar</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(getField(sub.keywords, sub.keywords_en, sub.keywords_ru) || '').split(',').filter(k => k.trim()).map((kw, i) => (
                <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 text-xs text-slate-700">{kw.trim()}</span>
              ))}
              {!(getField(sub.keywords, sub.keywords_en, sub.keywords_ru) || '').trim() && <span className="text-sm text-slate-400">-</span>}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Adabiyotlar</label>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{sub.references_list || '-'}</p>
          </div>
        </div>
      </div>

      {/* Files Section - Two Columns */}
      <div className="bg-white rounded-2xl border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Fayllar</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - Article files */}
            <div className="space-y-4">
              <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Maqola fayli</h4>
              {fileStoredName ? (
                <button
                  onClick={() => openPreview(fileStoredName, sub.file_name)}
                  className="w-full flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100 hover:bg-blue-100 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-blue-700 truncate">{sub.file_name || 'Maqola fayli'}</p>
                    <p className="text-xs text-blue-500">Ko'rish uchun bosing</p>
                  </div>
                </button>
              ) : (
                <p className="text-sm text-slate-400 p-4 bg-slate-50 rounded-xl border border-slate-100">Yuklanmagan</p>
              )}

              {/* Overleaf project link */}
              {sub.overleaf_project_id && (
                <a
                  href={`https://overleaf.uzjs.uz/project/${sub.overleaf_project_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:bg-emerald-100 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                    <span className="text-lg">📝</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-emerald-700">Overleaf loyiha</p>
                    <p className="text-xs text-emerald-500">Ochish uchun bosing</p>
                  </div>
                </a>
              )}

              {/* Revised file */}
              {revisedStoredName && (
                <div>
                  <h4 className="text-xs font-medium text-emerald-600 uppercase tracking-wider mb-2">Qayta ishlangan fayl</h4>
                  <button
                    onClick={() => openPreview(revisedStoredName, sub.revised_file_name)}
                    className="w-full flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100 hover:bg-emerald-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-emerald-700 truncate">{sub.revised_file_name || 'Qayta ishlangan fayl'}</p>
                      <p className="text-xs text-emerald-500">Ko'rish uchun bosing</p>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Right column - Payment receipt */}
            <div className="space-y-4">
              <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">To'lov habarnomasi</h4>
              {receiptStoredName ? (
                <div>
                  <button
                    onClick={() => openPreview(receiptStoredName, sub.payment_receipt_name)}
                    className="w-full flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100 hover:bg-amber-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" /></svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-amber-700 truncate">{sub.payment_receipt_name || "To'lov cheki"}</p>
                      <p className="text-xs text-amber-500">Ko'rish uchun bosing</p>
                    </div>
                  </button>
                  <div className="mt-3 flex items-center gap-3">
                    {sub.payment_status && <PaymentBadge status={sub.payment_status} />}
                    {(sub.payment_status === 'receipt_uploaded' || sub.payment_status === 'uploaded' || sub.payment_status === 'pending' || sub.payment_status === 'rejected') && (
                      <>
                        <button
                          onClick={handleConfirmPayment}
                          disabled={actionLoading}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                        >
                          ✅ Tasdiqlash
                        </button>
                        <button
                          onClick={handleRejectPayment}
                          disabled={actionLoading}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          ❌ Rad etish
                        </button>
                      </>
                    )}
                  </div>
                  {sub.payment_status === 'confirmed' && (
                    <div className="mt-3 bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                      <p className="text-sm text-emerald-700 font-medium">To'lov tasdiqlangan</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-400 p-4 bg-slate-50 rounded-xl border border-slate-100">Yuklanmagan</p>
                  {sub.payment_status && sub.payment_status !== 'none' && (
                    <div className="mt-3"><PaymentBadge status={sub.payment_status} /></div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* OJS ID */}
          {sub.ojs_submission_id && (
            <div className="mt-6 bg-violet-50 rounded-xl p-4 border border-violet-100">
              <label className="text-xs font-medium text-violet-700 uppercase tracking-wider">OJS ID</label>
              <p className="text-sm text-violet-800 mt-1 font-medium">{sub.ojs_submission_id}</p>
            </div>
          )}
        </div>
      </div>

      {/* Comments Section */}
      {(sub.admin_comment || sub.author_comment || sub.reviewer_comment) && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Izohlar</h3>
          </div>
          <div className="p-6 space-y-3">
            {sub.admin_comment && (
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <label className="text-xs font-medium text-amber-700 uppercase tracking-wider">Admin izohi</label>
                <p className="text-sm text-amber-800 mt-1">{sub.admin_comment}</p>
              </div>
            )}
            {sub.author_comment && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <label className="text-xs font-medium text-blue-700 uppercase tracking-wider">Muallif izohi</label>
                <p className="text-sm text-blue-800 mt-1">{sub.author_comment}</p>
              </div>
            )}
            {sub.reviewer_comment && (
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <label className="text-xs font-medium text-purple-700 uppercase tracking-wider">Taqrizchi izohi</label>
                <p className="text-sm text-purple-800 mt-1">{sub.reviewer_comment}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Persisted AI Review - always visible regardless of status */}
      {sub.ai_review_text && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
                <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>
              </div>
              <h3 className="text-sm font-semibold text-slate-900">Sun'iy intellekt taqrizi</h3>
            </div>
            {sub.ai_reviewed_at && (
              <span className="text-xs text-slate-400">{formatDate(sub.ai_reviewed_at)}</span>
            )}
          </div>
          <div className="p-6">
            <div className="bg-gradient-to-br from-violet-50 to-blue-50 rounded-xl p-5 border border-violet-200">
              <div className="prose prose-sm prose-slate max-w-none max-h-96 overflow-y-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{sub.ai_review_text}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Assignments */}
      {sub.review_assignments && sub.review_assignments.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Taqrizlar</h3>
          </div>
          <div className="p-6 space-y-3">
            {sub.review_assignments.map((review) => (
              <div key={review.id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-slate-900">{review.reviewer_name}</p>
                    <p className="text-xs text-slate-400">{review.reviewer_email}</p>
                  </div>
                  <div>
                    {review.review_result ? (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                        review.review_result === 'positive' ? 'bg-emerald-50 text-emerald-700' :
                        review.review_result === 'negative' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        {review.review_result === 'positive' ? 'Ijobiy' :
                         review.review_result === 'negative' ? 'Salbiy' : 'Qayta ishlash'}
                      </span>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={() => handleCompleteReview(review.id, 'positive')}
                          className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition-colors">Ijobiy</button>
                        <button onClick={() => handleCompleteReview(review.id, 'negative')}
                          className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors">Salbiy</button>
                        <button onClick={() => handleCompleteReview(review.id, 'revision')}
                          className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors">Qayta</button>
                      </div>
                    )}
                  </div>
                </div>
                {review.comment && <p className="text-sm text-slate-600 mt-2 bg-slate-50 rounded-lg p-2">{review.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* Actions Panel - at the bottom */}
      {/* ═══════════════════════════════════════════════ */}
      {sub.status !== 'published' && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Amallar</h3>
          </div>

          {/* AI Review Result Area */}
          {aiReview && (
            <div className="px-6 pt-4">
              <div className="bg-gradient-to-br from-violet-50 to-blue-50 rounded-xl p-5 border border-violet-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>
                  </div>
                  <h4 className="text-sm font-semibold text-violet-900">Sun'iy intellekt taqrizi</h4>
                </div>
                <div className="prose prose-sm prose-slate max-w-none max-h-96 overflow-y-auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiReview}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}

          {/* Reviewer Selection after AI Review */}
          {showReviewerSelect && (
            <div className="px-6 pt-4">
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">Taqrizchini tanlang</h4>
                {reviewers.length > 0 ? (
                  <div className="space-y-2">
                    {reviewers.map((rev) => (
                      <div key={rev.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{rev.last_name} {rev.first_name} {rev.middle_name || ''}</p>
                          <p className="text-xs text-slate-500">{rev.email} • {rev.affiliation || '-'}</p>
                        </div>
                        <button
                          onClick={() => handleSendToReviewer(rev)}
                          disabled={actionLoading}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          Yuborish
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Taqrizchilar topilmadi. Mualliflar sahifasida taqrizchi sifatida belgilang.</p>
                )}
              </div>
            </div>
          )}

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* 1. Taqrizga yuborish */}
              {(sub.status === 'submitted' || sub.status === 'revision_required') && (
                <button
                  onClick={handleAIReview}
                  disabled={actionLoading || aiReviewLoading}
                  className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-600 to-violet-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-violet-700 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    {aiReviewLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">
                      {aiReviewLoading
                        ? (aiCountdown > 0 ? `AI tahlil qilmoqda... (${aiCountdown}s)` : 'AI tahlil qilmoqda...')
                        : 'Taqrizga yuborish'}
                    </p>
                    <p className="text-xs text-white/70">
                      {aiReviewLoading ? 'Taxminan 45 soniya kutiladi' : 'AI tekshiruv + taqrizchiga yuborish'}
                    </p>
                  </div>
                </button>
              )}

              {/* 2. Maqolani qabul qilish */}
              {(sub.status === 'submitted' || sub.status === 'under_review') && (
                <button
                  onClick={handleAcceptDirect}
                  disabled={actionLoading}
                  className="flex items-center gap-3 p-4 bg-emerald-50 text-emerald-800 rounded-xl font-medium border border-emerald-200 hover:bg-emerald-100 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Maqolani qabul qilish</p>
                    <p className="text-xs text-emerald-600">Taqrizsiz keyingi bosqichga</p>
                  </div>
                </button>
              )}

              {/* 3. Kamchiliklarni tuzatish uchun qaytarish */}
              {(sub.status === 'submitted' || sub.status === 'under_review') && (
                <button
                  onClick={() => setCommentModal({ type: 'revision_required', title: 'Kamchiliklarni tuzatish uchun qaytarish' })}
                  disabled={actionLoading}
                  className="flex items-center gap-3 p-4 bg-amber-50 text-amber-800 rounded-xl font-medium border border-amber-200 hover:bg-amber-100 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Qaytarish</p>
                    <p className="text-xs text-amber-600">Izoh bilan qayta ishlashga</p>
                  </div>
                </button>
              )}

              {/* 4. Rad etish */}
              {sub.status !== 'accepted' && sub.status !== 'rejected' && (
                <button
                  onClick={() => setCommentModal({ type: 'rejected', title: 'Rad etish' })}
                  disabled={actionLoading}
                  className="flex items-center gap-3 p-4 bg-red-50 text-red-800 rounded-xl font-medium border border-red-200 hover:bg-red-100 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Rad etish</p>
                    <p className="text-xs text-red-600">Izoh bilan rad etish</p>
                  </div>
                </button>
              )}

              {/* Chop etishga yuborish (send to LaTeX/PDF) */}
              {sub.status === 'accepted' && (
                <button
                  onClick={handlePublish}
                  disabled={actionLoading}
                  className="flex items-center gap-3 p-4 bg-emerald-50 text-emerald-800 rounded-xl font-medium border border-emerald-200 hover:bg-emerald-100 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Chop etishga yuborish</p>
                    <p className="text-xs text-emerald-600">PDF/LaTeX oynasiga yuborish</p>
                  </div>
                </button>
              )}

              {/* Acceptance certificate download — only meaningful for accepted */}
              {sub.status === 'accepted' && (
                <button
                  onClick={() => handleDownloadCertificates('accepted')}
                  disabled={actionLoading}
                  className="flex items-center gap-3 p-4 bg-amber-50 text-amber-800 rounded-xl font-medium border border-amber-200 hover:bg-amber-100 transition-all disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Qabul sertifikati</p>
                    <p className="text-xs text-amber-600">Mualliflar uchun ZIP yuklab olish</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════ */}
      {/* Published Actions — DOI assignment */}
      {/* ═══════════════════════════════════════════════ */}
      {sub.status === 'published' && (
        <div className="bg-white rounded-2xl border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Nashr amallari</h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Page Numbers */}
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-slate-500 w-28">Sahifa raqamlari</label>
              <input
                type="number"
                min="1"
                placeholder="dan"
                defaultValue={sub.first_page || ''}
                className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                id="first_page_input"
              />
              <span className="text-slate-400">—</span>
              <input
                type="number"
                min="1"
                placeholder="gacha"
                defaultValue={sub.last_page || ''}
                className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                id="last_page_input"
              />
              <button
                onClick={async () => {
                  const fp = parseInt(document.getElementById('first_page_input').value) || null;
                  const lp = parseInt(document.getElementById('last_page_input').value) || null;
                  try {
                    await updateSubmissionMeta(sub.id, { first_page: fp, last_page: lp });
                    toast.success('Sahifa raqamlari saqlandi');
                    fetchSubmission();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Xatolik');
                  }
                }}
                className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Saqlash
              </button>
            </div>

            {/* DOI */}
            {sub.doi ? (
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">DOI tayinlangan</p>
                  <a href={`https://doi.org/${sub.doi}`} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline">
                    {sub.doi}
                  </a>
                  {sub.zenodo_record_url && (
                    <div>
                      <a href={sub.zenodo_record_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline block mt-1">
                        Zenodo recordini ochish
                      </a>
                    </div>
                  )}
                  {sub.doi_assigned_at && (
                    <p className="text-xs text-emerald-700 mt-1">
                      Tayinlangan sana: {formatDate(sub.doi_assigned_at)}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">DOI tayinlanmagan</p>
                  <a href="/#/latex" className="text-xs text-indigo-600 hover:underline">
                    Sonlar sahifasida DOI tayinlash →
                  </a>
                </div>
              </div>
            )}

            {/* Publication certificate download */}
            <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-emerald-800">Nashr sertifikatlari</p>
                <p className="text-xs text-emerald-700">Har bir muallif uchun alohida PDF — ZIP arxivda</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDownloadCertificates('published')}
                  disabled={actionLoading}
                  className="px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  📥 Nashr sertifikati
                </button>
                <button
                  onClick={() => handleDownloadCertificates('accepted')}
                  disabled={actionLoading}
                  className="px-3 py-2 bg-white border border-emerald-200 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  title="Qabul sertifikati (eski versiyani qayta yuklash)"
                >
                  Qabul
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal for Reject/Revision */}
      {commentModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setCommentModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">{commentModal.title}</h3>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const comment = e.target.elements.comment.value;
              if (!comment.trim()) { toast.error('Izoh kiriting'); return; }
              handleStatusChange(commentModal.type, comment);
            }}>
              <div className="p-6">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Izoh</label>
                <textarea
                  name="comment"
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  placeholder="Muallifga yuboriladi..."
                  autoFocus
                />
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                <button type="button" onClick={() => setCommentModal(null)}
                  className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
                  Bekor qilish
                </button>
                <button type="submit" disabled={actionLoading}
                  className={`px-5 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                    commentModal.type === 'rejected' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
                  }`}>
                  {actionLoading ? 'Yuborilmoqda...' : commentModal.type === 'rejected' ? 'Rad etish' : 'Qaytarish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <span className="text-sm font-medium text-slate-900 truncate">{previewFile.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <a href={previewFile.downloadUrl} download={previewFile.name}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Yuklab olish
                </a>
                <button onClick={closePreview}
                  className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-3 min-h-[60vh] bg-slate-50">
              {previewFile.type === 'image' ? (
                <div className="flex items-center justify-center h-full min-h-[60vh]">
                  <img src={previewFile.previewUrl} alt={previewFile.name} className="max-w-full max-h-[75vh] rounded-lg shadow-sm" />
                </div>
              ) : previewFile.type === 'pdf' ? (
                <iframe src={previewFile.previewUrl} className="w-full h-full min-h-[60vh] rounded-lg border-0" title="PDF preview" />
              ) : previewFile.type === 'docx' && previewFile.docxHtml ? (
                <div className="bg-white rounded-lg p-8 max-w-4xl mx-auto min-h-[60vh] prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewFile.docxHtml }} />
              ) : (
                <iframe src={previewFile.previewUrl} className="w-full h-full min-h-[60vh] rounded-lg border-0" title="File preview" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
